import { IEventDataSource } from "@/types/interfaces";
import { BigBashEvent, EventSchema, InputEventDetails } from "@/types/types";
import { typeConversionManager } from "@/utils";
import EventConflictError from "@/utils/EventConflictError";
import { showUserMessage, UserMessageBoxState } from "@components/UserMessageBox";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { z } from "zod";
import EventFormInputHandler from "./EventFormInputHandler";

const SUBMIT_SUCCESS_MESSAGE_DISPLAY_DURATION_MS = 1_500;

@customElement("event-form")
class EventForm extends LitElement {
	static readonly styles = css`
        .event-form {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .form-actions {
            display: flex;
            gap: 16px;
            justify-content: flex-end;
            margin-top: 16px;
            padding-top: 30px;
            border-top: 2px solid #f0f0f0;
        }
    `;

	@property({ type: Object }) event: BigBashEvent;
	@property({ type: Object }) eventDataSource: IEventDataSource;
	@property({ type: Boolean }) isUpdate: boolean;

	private eventInputDataChangeHandler: EventFormInputHandler;

	constructor(event: BigBashEvent, eventDataSource: IEventDataSource, isUpdate: boolean) {
		super();
		this.event = event;
		this.eventDataSource = eventDataSource;
		this.eventInputDataChangeHandler = new EventFormInputHandler(this.eventDataSource);
		this.isUpdate = isUpdate;
		this.addEventListener('input-change', this._handleDataChange);
		this.addEventListener('delete-team', this._handleDeleteTeam);
	}

	private readonly _handleDataChange = (event: CustomEvent<InputEventDetails>) => {
		if (!event.detail?.name || !event.detail?.value) {
			return;
		}
		this.event = this.eventInputDataChangeHandler.handleDataChange(this.event, event.detail);
	};

	private readonly _handleDeleteTeam = (event: CustomEvent<{ teamIndex: number }>) => {
		if (!this._confirmDelete(event.detail.teamIndex)) {
			return;
		}
		this.event = this.eventInputDataChangeHandler.handleDeleteTeam(this.event, event.detail.teamIndex);
	};

	private _confirmDelete(teamIndex: number) {
		return confirm(`Möchtest du das Team "${this.event.teams[teamIndex].name}" wirklich löschen?`);
	}

	private readonly _handleCancel = () => {
		window.location.href = '/host/events';
	};

	private readonly _handleSubmit = (event: BigBashEvent) => {
		const convertedEvent = typeConversionManager.convertEventObjectToValidTypes(this.event);
		if (this._isFormValid(convertedEvent)) {
			this._submitForm(convertedEvent);
		}
	};

	private _isFormValid(convertedEvent: BigBashEvent): boolean {
		try {
			EventSchema.parse(convertedEvent);
			return true;
		} catch (error) {
			if (error instanceof z.ZodError) {
				showUserMessage({
					state: UserMessageBoxState.ERROR,
					message: `Validierung fehlgeschlagen: ${error.issues.map(issue => issue.message).join(', ')}`,
					loading: false,
				});
			}
		}
		return false;
	}

	private readonly _submitForm = async (event: BigBashEvent): Promise<void> => {
		try {
			if (this.isUpdate) {
				await this.eventDataSource.updateEvent(event);
				showUserMessage({ state: UserMessageBoxState.SUCCESS, message: 'Event erfolgreich aktualisiert.', loading: false });
			} else {
				await this.eventDataSource.createEvent(event);
				showUserMessage({ state: UserMessageBoxState.SUCCESS, message: 'Event erfolgreich erstellt.', loading: false });
			}
			setTimeout((): void => {
				window.location.href = '/host/events';
			}, SUBMIT_SUCCESS_MESSAGE_DISPLAY_DURATION_MS);
		} catch (error) {
			this._handleSubmitError(error);
		}
	};

	private _handleSubmitError(error: unknown): void {
		if (error instanceof EventConflictError) {
			showUserMessage({ state: UserMessageBoxState.ERROR, message: 'Das Event wurde inzwischen von einem anderen Gerät geändert. Bitte lade die Seite neu und versuche es erneut.', loading: false });
		} else if (error instanceof Error) {
			showUserMessage({ state: UserMessageBoxState.ERROR, message: `Fehler beim Speichern des Events: ${error.message}`, loading: false });
		} else {
			showUserMessage({ state: UserMessageBoxState.ERROR, message: 'Beim Speichern des Events ist ein unbekannter Fehler aufgetreten.', loading: false });
		}
	}

	render() {
		if (!this.event) {
			return html`
				<div style="text-align: center; padding: 60px;">
					Loading form...
				</div>
			`;
		}

		return html`
                <form class="event-form" @submit="${this._handleSubmit}" id="eventForm">
                    <basic-info-section .values="${this.event}"></basic-info-section>
                    <!-- <contact-info-section .values="${this.event?.customer}"></contact-info-section> -->
                    <teams-section .values="${this.event?.teams}"></teams-section>
                    <notes-section .values="${this.event}"></notes-section>

                    <div class="form-actions">
                        <alert-button type="button" @click="${this._handleCancel}" class="default-button red-button">
                            Abbrechen
                        </alert-button>
                        <success-button class="default-button green-button" @click="${this._handleSubmit}">
                            ${this.isUpdate ? 'Aktualisieren' : 'Speichern'}
                        </success-button>
                    </div>
                </form>
            `;
	}
}

export default EventForm;